"use client";

/**
 * Date navigation for the panchang — the measured "biggest gap" against the
 * category leader, which ships a configurable instrument you return to while
 * ours was a static readout of today.
 *
 * The selected date is CLIENT STATE and deliberately does not touch the URL.
 * A `?date=` param (or worse, a `/panchang/<city>/<date>/` segment) would mint an
 * indexable near-duplicate per day per city — 365 x 198 of exactly the cohort
 * shape that got the domain re-scored (rule 66 #8 / ADR-0074). A city and a date
 * are SELECTORS. Shareable dated URLs are an owner decision, not a default, and
 * would need the canonical pinned to the bare path first.
 */

import { useMemo } from "react";

export const PANCHANG_DATE_NAV_TEST_ID = "panchang-date-navigator";

/** Civil date (YYYY-MM-DD) for `now` in a named IANA zone. */
export function civilDateInZone(instant: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/**
 * Shift a YYYY-MM-DD civil date by whole days.
 *
 * Anchored at UTC noon so a shift can never land on a DST transition and roll
 * back onto the same civil day — the classic off-by-one in date steppers.
 */
export function shiftCivilDate(date: string, days: number): string {
  const [year, month, day] = date.split("-").map(Number);
  const anchored = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  anchored.setUTCDate(anchored.getUTCDate() + days);
  return anchored.toISOString().slice(0, 10);
}

interface PanchangDateNavigatorProps {
  value: string;
  today: string;
  isHindi: boolean;
  onChange: (date: string) => void;
  /** How far either side of today the picker allows, in days. */
  rangeDays?: number;
}

const DEFAULT_RANGE_DAYS = 365 * 2;

export default function PanchangDateNavigator({
  value,
  today,
  isHindi,
  onChange,
  rangeDays = DEFAULT_RANGE_DAYS,
}: PanchangDateNavigatorProps) {
  const { min, max } = useMemo(
    () => ({ min: shiftCivilDate(today, -rangeDays), max: shiftCivilDate(today, rangeDays) }),
    [today, rangeDays]
  );
  const isToday = value === today;

  const label = {
    previous: isHindi ? "पिछला दिन" : "Previous day",
    next: isHindi ? "अगला दिन" : "Next day",
    today: isHindi ? "आज" : "Today",
    pick: isHindi ? "तारीख चुनें" : "Choose date",
  };

  return (
    <div
      data-testid={PANCHANG_DATE_NAV_TEST_ID}
      className="flex flex-wrap items-center justify-center gap-2 py-2"
    >
      <button
        type="button"
        aria-label={label.previous}
        onClick={() => onChange(shiftCivilDate(value, -1))}
        className="rounded-md border border-ish-line px-3 py-1.5 text-sm text-ish-dark hover:bg-ish-tint"
      >
        ‹
      </button>

      <label className="sr-only" htmlFor="panchang-date-input">
        {label.pick}
      </label>
      <input
        id="panchang-date-input"
        type="date"
        value={value}
        min={min}
        max={max}
        aria-label={label.pick}
        onChange={(event) => {
          const next = event.target.value;
          // An empty or cleared input must not blank the reading.
          if (next) onChange(next);
        }}
        className="rounded-md border border-ish-line px-3 py-1.5 text-sm text-ish-dark"
      />

      <button
        type="button"
        aria-label={label.next}
        onClick={() => onChange(shiftCivilDate(value, 1))}
        className="rounded-md border border-ish-line px-3 py-1.5 text-sm text-ish-dark hover:bg-ish-tint"
      >
        ›
      </button>

      <button
        type="button"
        onClick={() => onChange(today)}
        disabled={isToday}
        aria-disabled={isToday}
        className="rounded-md border border-ish-line px-3 py-1.5 text-sm text-ish-dark hover:bg-ish-tint disabled:opacity-40"
      >
        {label.today}
      </button>
    </div>
  );
}
